import {useFormik} from 'formik'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {LocationModel} from '../../models/acs/LocationModel'
import {TicketModelFulfillParams} from '../../models/ems/TicketModel'
import {GetLocationByCode, GetProductLocationsByCode} from '../../modules/default/acs/redux/AcsCRUD'
import {ApiTree} from '../../utils/Tree/ApiTree'
import {useAlerts} from '../alerts/useAlerts'
import {useOnChange} from '../hooks/useOnChange'
import {usePrevious} from '../hooks/usePrevious'
import {useSafeStateUpdate} from '../hooks/useSafeStateUpdate'
import {SeatMapSelectionModalInput} from '../inputs/SeatMapInput/SeatMapSelectionModalInput'
import {SeatMapValue} from '../inputs/SeatMapInput/SeatMapValue'
import {TreeNodeItem} from '../inputs/TreeSelect/TreeSelect'
import {useSeatMapPortalReservationState} from '../../modules/customer-portal/hooks/useSeatMapPortalReservationState'
import {GetvailableSeatMaps} from '../../modules/customer-portal/redux/CustomerPortalCRUD'
import {BookingDetailModel} from '../../models/customer-portal/BookingDetailModel'
export interface SeatMapPortalSelectionModalFormValues {
  selected: SeatMapValue
  locationCode: string
}

export interface SeatMapPortalSelectionModalFormProps {
  initialValues?: SeatMapPortalSelectionModalFormValues
  onSubmit: (values: SeatMapPortalSelectionModalFormValues) => void | Promise<void>
  open: boolean
  onHide: () => void
  product?: BookingDetailModel | null
  customerCode?: string
}

export const SeatMapPortalSelectionModalForm = ({
  onSubmit,
  initialValues = EMPTY_FORM_VALUES,
  onHide,
  open,
  product,
  customerCode,
}: SeatMapPortalSelectionModalFormProps) => {
  const [count, setCount] = useState(0)
  const {
    setAvailableSeates,
    seatMapSpacingX,
    extra,
    disabled,
    columns,
    active,
    rows,
    hidden,
    isRightToLeft,
    isBottomToTop,
    resetState: resetSeatMapState,
    isLoading,
    setIsLoading,
  } = useSeatMapPortalReservationState()
  const safeUpdate = useSafeStateUpdate()
  const previousInitialValues = usePrevious(initialValues)
  const previousOpen = usePrevious(open)
  const {push, pushError} = useAlerts()
  const [locations, setLocations] = useState<LocationModel[]>([])

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      await onSubmit(values)
    },
  })

  const handleSeatMapChange = useCallback(
    (value: SeatMapValue) => {
      formik.setFieldValue('selected', value)
    },
    [formik]
  )

  const resetLocationByCode = useCallback(
    async (locationCode: string, eventCode: string) => {
      const doneLoading = setIsLoading(locationCode)
      try {
        const {data} = await GetLocationByCode(locationCode)
        if (!data.seatMap) {
          push({
            message: `No seat maps available for ${data.name}`,
            variant: 'danger',
            timeout: 5000,
          })
        } else {
          safeUpdate(() => resetSeatMapState(data.seatMap))
        }
      } catch (e) {
        pushError(e)
      } finally {
        if (product && customerCode) {
          const {
            data: {availableSeats},
          } = await GetvailableSeatMaps(locationCode, product.code, eventCode, customerCode)
          setAvailableSeates(new SeatMapValue(availableSeats))
        }
        doneLoading()
      }
    },
    [
      setIsLoading,
      push,
      safeUpdate,
      resetSeatMapState,
      pushError,
      product,
      setAvailableSeates,
      customerCode,
    ]
  )

  const locationItems = useMemo((): TreeNodeItem[] => {
    const locationTree = new ApiTree(locations)
    return locationTree.getTreeSelectItems()
  }, [locations])

  const handleLocationChange = useCallback(
    (locationCode) => {
      formik.setFieldValue('locationCode', locationCode)
      formik.setFieldValue('selected', new SeatMapValue())
      resetSeatMapState()
      if (locationCode && product?.eventCode) {
        resetLocationByCode(locationCode, product.eventCode)
      }
    },
    [formik, resetSeatMapState, product, resetLocationByCode]
  )

  const resetLocationList = useCallback(async () => {
    setLocations([])
    if (product) {
      const {data} = await GetProductLocationsByCode(product.code)
      setLocations(data)
    }
  }, [product])

  useOnChange(product, () => {
    if (product) {
      setCount(product.bookingProductQty || 0)
      resetLocationList()
    }
  })

  useEffect(() => {
    if (previousInitialValues !== initialValues) {
      formik.resetForm()
    }
  }, [formik, initialValues, previousInitialValues])

  useEffect(() => {
    if (previousOpen !== open) {
      if (open) {
        formik.setValues(initialValues)
      } else {
        formik.setValues(EMPTY_FORM_VALUES)
      }
      resetSeatMapState()
    }
  }, [formik, initialValues, open, previousOpen, resetSeatMapState])

  const getModalTitle = useCallback(() => {
    return product?.name || ''
  }, [product?.name])

  const occupied = useMemo(() => {
    let activeSeats = new SeatMapValue()
    if (active && active.getSeatMapObject !== {}) {
      activeSeats = activeSeats.union(active)
      return new SeatMapValue(rows, columns).difference(activeSeats)
    }
  }, [active, columns, rows])

  return (
    <SeatMapSelectionModalInput
      modalTitle={getModalTitle}
      spacingX={seatMapSpacingX}
      occupied={occupied}
      extra={extra}
      locationItems={locationItems}
      locationCode={formik.values.locationCode}
      onLocationChange={handleLocationChange}
      onSubmit={formik.handleSubmit}
      count={count}
      disabled={disabled}
      columns={columns}
      rows={rows}
      loading={isLoading}
      disableSubmit={formik.isSubmitting || !formik.isValid}
      disableSelection={formik.isSubmitting}
      onChange={handleSeatMapChange}
      value={formik.values.selected}
      open={open}
      hidden={hidden}
      onHide={onHide}
      isRightToLeft={isRightToLeft}
      isBottomToTop={isBottomToTop}
    />
  )
}

const EMPTY_FORM_VALUES: SeatMapPortalSelectionModalFormValues = {
  selected: new SeatMapValue(),
  locationCode: '',
}

export const getPayload = (
  values: SeatMapPortalSelectionModalFormValues,
  productCode: string
): TicketModelFulfillParams => {
  const payload: TicketModelFulfillParams = {
    locationCode: values.locationCode,
    seats: values.selected.getSeatMapObject(),
    productCode,
  }

  return payload
}
